/**
 * AuthContext.tsx
 *
 * Authentication state management for EmCog.
 * Handles PIN verification, token storage, and auth state.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  expiresAt: number | null;
  authEnabled: boolean;
}

interface AuthContextType extends AuthState {
  verifyPin: (pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'emcog_auth_token';
const EXPIRES_STORAGE_KEY = 'emcog_auth_expires';

// Get base URL for API calls (also the connection's session check in useWebSocket)
export function getApiBaseUrl(): string {
  // In production, use same origin
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // In development, use the backend port
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    expiresAt: null,
    authEnabled: true // Assume enabled until we check
  });

  // Check auth status and validate stored token on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        const baseUrl = getApiBaseUrl();

        // First, check if auth is enabled
        const statusRes = await fetch(`${baseUrl}/api/auth/status`);
        const status = await statusRes.json();

        if (!status.enabled) {
          // Auth not enabled, allow access
          setState({
            isAuthenticated: true,
            isLoading: false,
            token: null,
            expiresAt: null,
            authEnabled: false
          });
          return;
        }

        // Auth is enabled, check for stored token
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedExpires = localStorage.getItem(EXPIRES_STORAGE_KEY);

        if (!storedToken) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            authEnabled: true
          }));
          return;
        }

        // Check if token is expired locally
        if (storedExpires && parseInt(storedExpires) < Date.now()) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(EXPIRES_STORAGE_KEY);
          setState(prev => ({
            ...prev,
            isLoading: false,
            authEnabled: true
          }));
          return;
        }

        // Validate token with backend
        const validateRes = await fetch(`${baseUrl}/api/auth/validate`, {
          headers: {
            'Authorization': `Bearer ${storedToken}`
          }
        });
        const validation = await validateRes.json();

        if (validation.valid) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            token: storedToken,
            expiresAt: storedExpires ? parseInt(storedExpires) : null,
            authEnabled: true
          });
        } else {
          // Token invalid, clear storage
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(EXPIRES_STORAGE_KEY);
          setState(prev => ({
            ...prev,
            isLoading: false,
            authEnabled: true
          }));
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // On error, assume auth is disabled (backend might be down)
        setState({
          isAuthenticated: true,
          isLoading: false,
          token: null,
          expiresAt: null,
          authEnabled: false
        });
      }
    }

    checkAuth();
  }, []);

  // Verify PIN with backend
  const verifyPin = useCallback(async (pin: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const baseUrl = getApiBaseUrl();

      const res = await fetch(`${baseUrl}/api/auth/pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin })
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || 'Invalid PIN' };
      }

      // Store token
      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      localStorage.setItem(EXPIRES_STORAGE_KEY, data.expiresAt.toString());

      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        token: data.token,
        expiresAt: data.expiresAt
      }));

      return { success: true };
    } catch (error) {
      console.error('PIN verification failed:', error);
      return { success: false, error: 'Connection failed' };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(EXPIRES_STORAGE_KEY);
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      token: null,
      expiresAt: null
    }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, verifyPin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper to get stored token for WebSocket connections
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}
